import { flow } from 'es-toolkit';
import PropTypes from 'prop-types';
import { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { mintSingleton } from 'src/client/mint';
import { fetchMe } from '../actions/me';
import withAuth from '../decorators/auth/withAuth';
import withContextProviders from '../decorators/withContextProviders';
import withDesktopApp from '../decorators/withDesktopApp';
import withMobileJourney from '../decorators/withMobileJourney';
import useClientAnalytics from '../hooks/useClientAnalytics';
import useOneTrust from '../hooks/useOneTrust';
import { useRouteProps } from '../providers/RoutePropsProvider';
import { selectUserAgent } from '../selectors/app';
import connectAsync from '../utils/connectAsync';
import AppRootLoader from './AppRootLoader';
import { DiscordSplash, useDiscord } from './discord';
import MobileWebEntryLoader from './MobileWebEntryLoader';
import withGuideItemProfile from './withGuideItemProfile';

const AppConnectorComponent = memo((props) => {
  const { onHistoryUpdate, location, match, route, shouldShowMobileOverlay, history } = props;
  const routeProps = useRouteProps();
  const userAgent = useSelector(selectUserAgent);
  const MobileWebLandingComponent = useMemo(
    () =>
      routeProps.guideContext.guideId
        ? withGuideItemProfile(MobileWebEntryLoader)
        : MobileWebEntryLoader,
    [routeProps.guideContext.guideId],
  );
  const { isDiscordReady, showDiscordSplashScreen, splashScreenType } = useDiscord(history);
  const contentProps = {
    route,
    location,
    history,
    isDiscordReady,
  };

  if (routeProps.hideAds) {
    mintSingleton.disable();
  }

  useOneTrust({ isMobileBlockerFlow: shouldShowMobileOverlay });
  useClientAnalytics({
    onHistoryUpdate,
    hasNonAdEligibleOverlay: shouldShowMobileOverlay,
  });

  if (showDiscordSplashScreen) {
    return <DiscordSplash splashScreenType={splashScreenType} />;
  }

  if (shouldShowMobileOverlay) {
    return (
      <MobileWebLandingComponent
        match={match}
        isLanding={routeProps.isLanding}
        location={location}
        userAgent={userAgent}
      />
    );
  }

  return <AppRootLoader {...contentProps} />;
});

AppConnectorComponent.propTypes = {
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  route: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  store: PropTypes.object,
  onHistoryUpdate: PropTypes.func,
  shouldShowMobileOverlay: PropTypes.bool,
};

const AppConnector = flow(
  withMobileJourney,
  withAuth,
  connectAsync([fetchMe]),
  withDesktopApp,
  withContextProviders,
)(AppConnectorComponent);

export default AppConnector;
